import React from 'react';
import { Button, CircularProgress, Divider, Typography } from '@mui/material';
import { Order } from '@m4a3/api-client';
import { formatTimeAgo } from '../../../utils/utils';
import { getStatusData } from '../../../services/order-service';
import {
  getEmojiForCollectorStatus,
  getDescriptionForCollectorStatus,
} from '../types/CollectorStatus';
import { OrderActivityRow } from './OrderActivityRow';
import { OrderCardStatusIndicator } from './OrderCardStatusIndicator';
import { useTimeTicker } from '../../../contexts/TimeTickerContext';

type OrderActivityTimelineProps = {
  order: Order | null | undefined;
  history: any[];
  isLoading: boolean;
  onOpenExpectedTimetable?: () => void;
  onOpenRouteConfirmation?: (order: Order) => void;
  openSuggestModal?: () => void;
  onAssignPad?: (e: React.MouseEvent) => void;
  linkedOrdersForOverweightOrders?: {
    linkedOverweightOrderIds?: string[];
    color?: string;
  } | null;
  filterWarningRows?: boolean;
};

const getHistoryTimestamp = (item: any): string | null => {
  return (
    item?.timestamp ||
    item?.createDate ||
    item?.createdAt ||
    item?.time ||
    null
  );
};

// Format a timestamp as a static time-of-day string, e.g. "09:04 AM"
const formatTimeOfDay = (timestamp: string | null): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getHistoryDisplay = (
  item: any,
  order: Order | null | undefined
): { title: string; subtitle: string; statusKey?: string; collectorStatusKey?: string } => {
  if (item?.collectorStatus || item?.changeType === 'COLLECTOR') {
    const collectorStatus = item.collectorStatus || item.status;

    const emoji = getEmojiForCollectorStatus(collectorStatus);
    const title = getDescriptionForCollectorStatus(
      collectorStatus,
      order?.collectorId
    );
    const timestamp = getHistoryTimestamp(item);

    return {
      title,
      subtitle: timestamp ? formatTimeAgo(timestamp) : '',
      collectorStatusKey: collectorStatus,
    };
  }

  // Bag location / weighing events - treat as ORDER-type events with a friendly label
  // Example ORDERFUL history shapes:
  // - { changeType: 'ORDER', status: 'bagLocations', ... }
  // - { changeType: 'ORDER', bagLocations: [...], ... }
  if (
    (item?.changeType === 'ORDER' && item?.status === 'bagLocations') ||
    (item?.bagLocations && Array.isArray(item.bagLocations) && item.bagLocations.length > 0)
  ) {
    const timestamp = getHistoryTimestamp(item);

    return {
      title: 'Order Weighed',
      subtitle: timestamp ? formatTimeAgo(timestamp) : '',
      statusKey: 'ORDER_WEIGHED',
    };
  }

  // Warning events (e.g. ARRIVAL_LATE_WARNING, PICKUP_LATE_WARNING, COLLECTOR_LEAVE_LATE_WARNING)
  if (item?.changeType === 'WARNING' && item?.status) {
    const warningLabels: Record<string, string> = {
      ARRIVAL_LATE_WARNING: 'Collector drop-off late',
      PICKUP_LATE_WARNING: 'Collector collection late',
      COLLECTOR_LEAVE_LATE_WARNING: 'Collector left late',
      STORE_ACCEPTED_LATE_WARNING: 'Store accepted late',
      HIGH_ARRIVAL_LATE_WARNING: 'Collector drop-off late',
      HIGH_PICKUP_LATE_WARNING: 'Collector collection late',
      HIGH_COLLECTOR_LEAVE_LATE_WARNING: 'Collector left late',
      HIGH_COLLECTOR_WAITING_AT_MERCHANT_LATE_WARNING: 'Collector late to store',
      STORE_PREPARED_LATE_WARNING: 'Store prepared late',
      HIGH_STORE_PREPARED_LATE_WARNING: 'Store prepared late'
    };

    const timestamp = getHistoryTimestamp(item);
    
    // Use expectedTimestamp for the subtitle if available ("Expected N minutes ago")
    let subtitle = timestamp ? formatTimeAgo(timestamp) : '';
    if (item.expectedTimestamp) {
      subtitle = `Expected ${formatTimeAgo(item.expectedTimestamp)}`;
    }

    return {
      title: warningLabels[item.status] || item.status,
      subtitle,
      statusKey: item.status
    };
  }

  // Order status history
  if (item?.status) {
    const statusData = getStatusData(item.status);

    const timestamp = getHistoryTimestamp(item);
    const subtitle = timestamp ? formatTimeAgo(timestamp) : '';

    // Override title for dispatched -> "Flying to {eircode}"
    const title =
      item.status === 'DISPATCHED' && order?.eircode
        ? `Flying to ${order.eircode}`
        : statusData.text || item.status;

    return {
      title,
      subtitle,
      statusKey: item.status,
    };
  }

  const timestamp = getHistoryTimestamp(item);
  const subtitle = timestamp ? formatTimeAgo(timestamp) : '';

  return {
    title: item?.title || item?.description || 'Updated',
    subtitle,
  };
};

export const OrderActivityTimeline: React.FC<OrderActivityTimelineProps> = ({
  order,
  history,
  isLoading,
  onOpenExpectedTimetable,
  onOpenRouteConfirmation,
  openSuggestModal,
  onAssignPad,
  linkedOrdersForOverweightOrders,
  filterWarningRows = true,
}) => {
  // Use global time ticker instead of individual interval
  const { currentTime } = useTimeTicker();
  const tick = currentTime; // Use currentTime as tick for re-renders

  const sortedHistory = React.useMemo(() => {
    if (!history || history.length === 0) {
      return [];
    }

    let processedHistory = [...history].filter(item => {
      // Filter out rows where status is a JSON string (e.g. "{\"orderUIDFromApp\":...}")
      if (typeof item.status === 'string' && item.status.trim().startsWith('{')) {
        return false;
      }
      return true;
    });

    if (filterWarningRows) {
        processedHistory = processedHistory.filter(item => {
            const isHighWarningStatus =
            item.status === 'HIGH_ARRIVAL_LATE_WARNING' ||
            item.status === 'HIGH_PICKUP_LATE_WARNING' ||
            item.status === 'HIGH_COLLECTOR_LEAVE_LATE_WARNING' ||
            item.status === 'HIGH_COLLECTOR_WAITING_AT_MERCHANT_LATE_WARNING' ||
            item.status === 'HIGH_STORE_PREPARED_LATE_WARNING';

            const isYellowWarningStatus =
            item.status === 'ARRIVAL_LATE_WARNING' ||
            item.status === 'PICKUP_LATE_WARNING' ||
            item.status === 'COLLECTOR_LEAVE_LATE_WARNING' ||
            item.status === 'STORE_ACCEPTED_LATE_WARNING' ||
            item.status === 'STORE_PREPARED_LATE_WARNING';
            
            return !isHighWarningStatus && !isYellowWarningStatus;
        });
    }

    return processedHistory.sort((a, b) => {
      const ta = getHistoryTimestamp(a);
      const tb = getHistoryTimestamp(b);
      if (!ta || !tb) return 0;
      // Most recent first
      return new Date(tb).getTime() - new Date(ta).getTime();
    });
  }, [history, tick, filterWarningRows]);

  // Find the index of the first row that is NOT a warning and NOT inactive.
  // This row will be the one to show the "live count up" timer.
  const activeStatusIndex = React.useMemo(() => {
    return sortedHistory.findIndex(item => {
      const isHighWarningStatus =
        item.status === 'HIGH_ARRIVAL_LATE_WARNING' ||
        item.status === 'HIGH_PICKUP_LATE_WARNING' ||
        item.status === 'HIGH_COLLECTOR_LEAVE_LATE_WARNING' ||
        item.status === 'HIGH_COLLECTOR_WAITING_AT_MERCHANT_LATE_WARNING' ||
        item.status === 'HIGH_STORE_PREPARED_LATE_WARNING';

      const isYellowWarningStatus =
        item.status === 'ARRIVAL_LATE_WARNING' ||
        item.status === 'PICKUP_LATE_WARNING' ||
        item.status === 'COLLECTOR_LEAVE_LATE_WARNING' ||
        item.status === 'STORE_ACCEPTED_LATE_WARNING' ||
        item.status === 'STORE_PREPARED_LATE_WARNING';

      const isWarningRow = isHighWarningStatus || isYellowWarningStatus;
      
      const isInactive = 
        item.status === 'COMPLETE' ||
        item.status === 'RESET' ||
        item.status === 'CANCELLED';

      return !isWarningRow && !isInactive;
    });
  }, [sortedHistory]);

  if (!order) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Happening now */}
      <div style={{ marginBottom: 12 }}>
        <Typography
          variant="subtitle2"
          style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}
        >
          Happening now
        </Typography>
        <OrderCardStatusIndicator
          order={order}
          onOpenRouteConfirmation={onOpenRouteConfirmation}
          openSuggestModal={openSuggestModal}
          onAssignPad={onAssignPad}
          linkedOrdersForOverweightOrders={linkedOrdersForOverweightOrders}
        />
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Past activity */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 700, fontSize: 13 }}
          >
            Past activity
          </Typography>
          {onOpenExpectedTimetable ? (
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                onOpenExpectedTimetable();
              }}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                minWidth: 0,
                padding: '2px 6px'
              }}
            >
              Expected timetable
            </Button>
          ) : null}
        </div>

        {isLoading && (!sortedHistory || sortedHistory.length === 0) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 0',
            }}
          >
            <CircularProgress size={18} />
          </div>
        )}

        {!isLoading && (!sortedHistory || sortedHistory.length === 0) && (
          <Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>
            No past activity yet
          </Typography>
        )}

        <div
          style={{
            marginTop: 4,
            maxHeight: '100%',
            overflowY: 'auto',
            paddingRight: 4,
          }}
        >
          {sortedHistory.map((item, index) => {
            const { title, subtitle, statusKey, collectorStatusKey } = getHistoryDisplay(item, order);

            const timestamp = getHistoryTimestamp(item);
            const ageMinutes =
              timestamp != null
                ? Math.floor(
                    (new Date().getTime() - new Date(timestamp).getTime()) /
                      (1000 * 60)
                  )
                : null;

            // For non-latest rows (those that show a fixed time-of-day), compute how long
            // this status lasted until the next status change (the more recent item above it).
            let gapMinutes: number | null = null;
            if (index > 0) {
              const newerItem = sortedHistory[index - 1];
              const newerTimestamp = getHistoryTimestamp(newerItem);
              if (timestamp && newerTimestamp) {
                const diffMs =
                  new Date(newerTimestamp).getTime() - new Date(timestamp).getTime();
                if (diffMs > 0) {
                  gapMinutes = Math.floor(diffMs / (1000 * 60));
                }
              }
            }

            // Determine if the top entry is an inactive status that should NOT
            // show live countdown or warning colours.
            const isTopInactiveStatus =
              index === 0 &&
              (statusKey === 'COMPLETE' ||
                statusKey === 'RESET' ||
                statusKey === 'CANCELLED');

            // Thresholds:
            // - Yellow state: 15+ minutes (default)
            // - Red state: 20+ minutes (default)
            //
            // Custom thresholds:
            // - STORE_ACCEPTED: Yellow 25+, Red 35+
            
            let yellowThreshold = 15;
            let redThreshold = 20;

            if (statusKey === 'STORE_ACCEPTED') {
              yellowThreshold = 25;
              redThreshold = 35;
            }
            
            // For the most recent row (index 0), we base this on age since the event.
            // For older rows, we base it on how long that status lasted until the next one.
            const isYellowByAge =
              index === 0 &&
              !isTopInactiveStatus &&
              ageMinutes != null &&
              ageMinutes >= yellowThreshold &&
              ageMinutes < redThreshold;
            const isRedByAge =
              index === 0 &&
              !isTopInactiveStatus &&
              ageMinutes != null &&
              ageMinutes >= redThreshold;

            const isYellowByGap =
              index > 0 && gapMinutes != null && gapMinutes >= yellowThreshold && gapMinutes < redThreshold;
            const isRedByGap =
              index > 0 && gapMinutes != null && gapMinutes >= redThreshold;

            // Status-based overrides for warning events:
            // - HIGH_* warning statuses should always be red
            // - Non-HIGH warning statuses should be yellow
            const isHighWarningStatus =
              statusKey === 'HIGH_ARRIVAL_LATE_WARNING' ||
              statusKey === 'HIGH_PICKUP_LATE_WARNING' ||
              statusKey === 'HIGH_COLLECTOR_LEAVE_LATE_WARNING' ||
              statusKey === 'HIGH_COLLECTOR_WAITING_AT_MERCHANT_LATE_WARNING' ||
              statusKey === 'HIGH_STORE_PREPARED_LATE_WARNING';

            const isYellowWarningStatus =
              statusKey === 'ARRIVAL_LATE_WARNING' ||
              statusKey === 'PICKUP_LATE_WARNING' ||
              statusKey === 'COLLECTOR_LEAVE_LATE_WARNING' ||
              statusKey === 'STORE_ACCEPTED_LATE_WARNING' ||
              statusKey === 'STORE_PREPARED_LATE_WARNING';

            const isWarningRow = isHighWarningStatus || isYellowWarningStatus;

            const shouldHighlightYellow =
              isYellowByAge || isYellowByGap || isYellowWarningStatus;
            const shouldHighlightRed =
              isRedByAge || isRedByGap || isHighWarningStatus;

            // Use the same warning colour system as OrderCardStatusIndicator
            const backgroundColor =
              shouldHighlightRed
                ? '#FEE4E2'
                : shouldHighlightYellow
                ? '#FEF3C7'
                : undefined;
            const iconBubbleBackgroundColor =
              shouldHighlightRed
                ? '#E61A27'
                : shouldHighlightYellow
                ? '#F79009'
                : undefined;
            const subtitleColor =
              shouldHighlightRed
                ? '#E61A27'
                : shouldHighlightYellow
                ? '#DC6803'
                : undefined;

            // For warning rows (e.g. HIGH_ARRIVAL_LATE_WARNING), we generally want to show "Expected X mins ago"
            // or some static text rather than a live "happened X mins ago" count up, because the event
            // is a point-in-time warning.
            
            // We want to show the live count-up timer ("X mins ago") on the *top-most valid status row*,
            // even if it's pushed down by warning rows.
            // All other rows show the static time (e.g. "09:04 AM").
            const showLiveTimer = index === activeStatusIndex;

            const baseSubtitle =
              showLiveTimer
                ? `${formatTimeAgo(timestamp)}` // Will show e.g. "2 mins ago"
                : (index === 0 && !isTopInactiveStatus && isWarningRow) // Special handling for top warning row to keep its subtitle
                ? subtitle
                : formatTimeOfDay(timestamp);

            // For older rows, if the status lasted 10+ minutes until the next status,
            // append explanatory text.
            let displaySubtitle = baseSubtitle;

            // For past warning cards (not the top/latest one), show "Late by X mins"
            // if the data is available.
            // If it's a warning row, we do NOT want to show the original timestamp if lateByMins is present,
            // we ONLY want to show "Late by X mins".
            if (
              (isHighWarningStatus || isYellowWarningStatus) &&
              item.lateByMins != null
            ) {
              displaySubtitle = `Late by ${item.lateByMins} mins`;
            } else if (
              !showLiveTimer &&
              (isRedByGap || isYellowByGap) &&
              gapMinutes != null
            ) {
                displaySubtitle = `${baseSubtitle} · Took ${gapMinutes} mins`;
            }

            return (
              <OrderActivityRow
                key={item?.id || `${title}-${index}`}
                statusKey={statusKey}
                collectorStatusKey={collectorStatusKey}
                iconBubbleBackgroundColor={iconBubbleBackgroundColor}
                iconColorOverride={
                  shouldHighlightRed || shouldHighlightYellow ? '#FFFFFF' : undefined
                }
                collectorId={order?.collectorId}
                eircode={(order as any)?.eircode}
                title={title}
                subtitle={displaySubtitle}
                backgroundColor={backgroundColor}
                subtitleColor={subtitleColor}
                isRequiredAction={false}
                showChevron={false}
                hasAction={false}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};


